import {useMemo} from 'react'
import type {LogEntry} from '../types/session'
import {isCompletionToolMessage, isToolOutputsMessage} from '../types/session'
import {isAbortMessage} from '../types/session-events'
import type {PendingPromptResult} from '../components/session-logs/prompt-log-utils'
import {extractPromptFromEntry, getPromptToolCallId} from '../components/session-logs/prompt-log-utils'

const NO_PENDING_PROMPT: PendingPromptResult = {
  pendingPrompt: null,
  pendingToolCallId: null,
  hasPendingPrompt: false,
}

/**
 * Build a set of all tool call IDs that have been resolved (responded to or aborted).
 * Single O(N) pass over logs.
 */
function buildResolvedToolCallIds(logs: LogEntry[]): Set<string> {
  const resolved = new Set<string>()
  // Track prompt IDs we've seen so aborts only resolve prompts that precede them
  const seenPromptIds = new Set<string>()

  for (const entry of logs) {
    // Direct tool response
    if (isCompletionToolMessage(entry)) {
      resolved.add(entry.tool_call_id)
      continue
    }
    if (isToolOutputsMessage(entry)) {
      resolved.add(entry.toolCallId)
      continue
    }

    // Track when prompts appear
    const promptId = getPromptToolCallId(entry)
    if (promptId) {
      seenPromptIds.add(promptId)
      continue
    }

    // Abort resolves all prompts seen so far
    if (isAbortMessage(entry)) {
      for (const id of seenPromptIds) {
        resolved.add(id)
      }
    }
  }

  return resolved
}

/**
 * Computes the earliest unresolved interactive prompt from session logs.
 * Uses FIFO ordering so that when multiple prompts are pending,
 * the user addresses them in the order they arrived.
 */
export function computePendingPrompt(logs: LogEntry[] | undefined): PendingPromptResult {
  if (!logs?.length) {
    return NO_PENDING_PROMPT
  }

  const resolvedIds = buildResolvedToolCallIds(logs)

  // Find the earliest unresolved prompt (FIFO queue behavior)
  for (const entry of logs) {
    const prompt = extractPromptFromEntry(entry)
    if (prompt && !resolvedIds.has(prompt.toolCallId)) {
      return {
        pendingPrompt: prompt,
        pendingToolCallId: prompt.toolCallId,
        hasPendingPrompt: true,
      }
    }
  }

  return NO_PENDING_PROMPT
}

/**
 * Hook that detects the earliest unresolved CLI prompt from session logs.
 *
 * Used to:
 * 1. Filter pending prompts from inline log display
 * 2. Show pending prompts prominently in chat input area
 *
 * @param logs - The session log entries to analyze
 * @returns The pending prompt info, or null values if no pending prompt
 */
export function usePendingPrompt(logs: LogEntry[] | undefined): PendingPromptResult {
  return useMemo(() => computePendingPrompt(logs), [logs])
}
